import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { RefreshCcw, Play, BarChart2, ChevronDown, ChevronUp, X, ChevronLeft, ChevronRight, Settings, Trash2, Square, Download } from 'lucide-react';
import ImageModal from './components/ImageModal';
import { toBool, toFiniteCount, toFiniteNumber, formatResizeDisplay, formatStartedAt, formatTokenCount, formatEta, getItemExplanation as getItemExplanationText, BOUNDING_BOX_COLORS } from './utils/formatters';
import { useRuntimeSettings } from './context/RuntimeSettingsContext';
import {
  normalizeRunConfig, normalizeActiveRuns, mergeActiveRunsForward,
  getResultTokenCounts, getResultAttemptSourceInfo, getSourceDisplayLabel, getRunCacheStats, getRunTokenTotals,
  getRunProgressMetrics, getRunPointStats, formatRunDataPoints, getRunDataPointsHint,
  withRagPromptPreview,
  getTrustedReviewNote,
  computeAccuracyRatio, uniqueNonEmptyUrls, normalizeImageKey,
  getResultLotNumber, getResultImageUrls, getDisabledImageKeySet,
  getLotActionKey, getImageActionKey, getReviewNoteDraftKey,
  escapeCsvCell, toSafeFilenamePart, buildRunExportRows, exportRunToCsv,
} from './utils/testRigHelpers';

const TestRigPage = () => {
  const [testResults, setTestResults] = useState([]);
  const [isRunning, setIsRunning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataLoadError, setDataLoadError] = useState('');
  const [expandedRun, setExpandedRun] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedLotNum, setSelectedLotNum] = useState(null);
  const [selectedItems, setSelectedItems] = useState(null);
  const {
    modelName,
    setModelName,
    resizeMode,
    setResizeMode,
    compressionMode,
    setCompressionMode,
    useEbayRag,
    setUseEbayRag,
    useNotesRag,
    setUseNotesRag,
  } = useRuntimeSettings();
  const [isChangingModel, setIsChangingModel] = useState(false);
  const [isChangingResizeMode, setIsChangingResizeMode] = useState(false);
  const [isChangingCompressionMode, setIsChangingCompressionMode] = useState(false);
  const [isChangingEbayRag, setIsChangingEbayRag] = useState(false);
  const [isChangingNotesRag, setIsChangingNotesRag] = useState(false);
  const [codePromptTemplates, setCodePromptTemplates] = useState({
    ebay_off_notes_off: '',
    ebay_on_notes_off: '',
    ebay_off_notes_on: '',
    ebay_on_notes_on: '',
  });
  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [datasetSearch, setDatasetSearch] = useState('');
  const [isLoadingDatasets, setIsLoadingDatasets] = useState(false);
  const [isRefreshingDatasets, setIsRefreshingDatasets] = useState(false);
  const [datasetLoadError, setDatasetLoadError] = useState('');
  const [isDatasetDropdownOpen, setIsDatasetDropdownOpen] = useState(false);
  const [runModelSearchQuery, setRunModelSearchQuery] = useState('');
  const [runDatasetSearchQuery, setRunDatasetSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [activeRuns, setActiveRuns] = useState([]);
  const [isStartingRun, setIsStartingRun] = useState(false);
  const [terminatingRunIds, setTerminatingRunIds] = useState({});
  const [isTerminatingAllRuns, setIsTerminatingAllRuns] = useState(false);
  const [isFlushing, setIsFlushing] = useState(false);
  const [lotImageActionBusy, setLotImageActionBusy] = useState({});
  const [rerunningLotBusy, setRerunningLotBusy] = useState({});
  const [reviewNoteDrafts, setReviewNoteDrafts] = useState({});
  const [savingReviewNotes, setSavingReviewNotes] = useState({});
  const [lotNoteDrafts, setLotNoteDrafts] = useState({});
  const [savingLotNotes, setSavingLotNotes] = useState({});
  const [expandedExplanations, setExpandedExplanations] = useState({});
  const [expandedItemNames, setExpandedItemNames] = useState({});
  const [expandedHistory, setExpandedHistory] = useState({});
  const datasetDropdownRef = useRef(null);
  const fetchSequenceRef = useRef(0);
  const latestAppliedFetchSequenceRef = useRef(0);
  const isRunInProgress = activeRuns.length > 0;
  const shouldPollRunStatus = isRunInProgress || isRunning === true || isStartingRun;
  const getSourceBadgeClassName = (sourceValue, cacheHitValue) => {
    if (cacheHitValue === true) return 'bg-cyan-100 text-cyan-700';
    if (cacheHitValue === false) return 'bg-slate-200 text-slate-700';
    if (sourceValue === 'cache') return 'bg-cyan-100 text-cyan-700';
    if (sourceValue === 'provider') return 'bg-slate-200 text-slate-700';
    return 'bg-amber-100 text-amber-700';
  };
  const RERUN_TIMEOUT_POLL_INTERVAL_MS = 3000;
  const RERUN_TIMEOUT_POLL_WINDOW_MS = 180000;

  const fetchData = async ({ showLoading = true } = {}) => {
    const requestSequence = ++fetchSequenceRef.current;
    let shouldApply = true;

    if (showLoading) {
      setLoading(true);
    }
    setDataLoadError('');
    try {
      const statusRes = await axios.get('/test_status');
      const statusPayload = statusRes?.data || {};
      const promptTemplatesPayload = (statusPayload?.prompt_templates && typeof statusPayload.prompt_templates === 'object')
        ? statusPayload.prompt_templates
        : {};
      const rawActiveRuns = Array.isArray(statusPayload?.active_runs)
        ? statusPayload.active_runs
        : (Array.isArray(statusPayload?.run_contexts)
          ? statusPayload.run_contexts
          : ((statusPayload?.run_context && typeof statusPayload.run_context === 'object') ? [statusPayload.run_context] : []));
      const normalizedActiveRuns = normalizeActiveRuns(rawActiveRuns);
      const nextIsRunning = statusPayload.is_running === true || normalizedActiveRuns.length > 0;

      if (requestSequence < latestAppliedFetchSequenceRef.current) {
        shouldApply = false;
      } else {
        latestAppliedFetchSequenceRef.current = requestSequence;
        setCodePromptTemplates((previous) => ({
          ebay_off_notes_off: typeof promptTemplatesPayload?.ebay_off_notes_off === 'string'
            ? promptTemplatesPayload.ebay_off_notes_off
            : (
              typeof promptTemplatesPayload?.ebay_rag_off === 'string'
                ? promptTemplatesPayload.ebay_rag_off
                : previous.ebay_off_notes_off
            ),
          ebay_on_notes_off: typeof promptTemplatesPayload?.ebay_on_notes_off === 'string'
            ? promptTemplatesPayload.ebay_on_notes_off
            : (
              typeof promptTemplatesPayload?.ebay_rag_on === 'string'
                ? promptTemplatesPayload.ebay_rag_on
                : previous.ebay_on_notes_off
            ),
          ebay_off_notes_on: typeof promptTemplatesPayload?.ebay_off_notes_on === 'string'
            ? promptTemplatesPayload.ebay_off_notes_on
            : previous.ebay_off_notes_on,
          ebay_on_notes_on: typeof promptTemplatesPayload?.ebay_on_notes_on === 'string'
            ? promptTemplatesPayload.ebay_on_notes_on
            : previous.ebay_on_notes_on,
        }));
        setActiveRuns((previous) => mergeActiveRunsForward(previous, normalizedActiveRuns));
        setIsRunning(nextIsRunning || normalizedActiveRuns.length > 0);
      }

      const resultsRes = await axios.get('/get_test_results');
      if (requestSequence < latestAppliedFetchSequenceRef.current) {
        shouldApply = false;
      } else {
        latestAppliedFetchSequenceRef.current = requestSequence;
        setTestResults(Array.isArray(resultsRes.data) ? resultsRes.data : []);
      }
    } catch (error) {
      if (requestSequence < latestAppliedFetchSequenceRef.current) {
        shouldApply = false;
      } else {
        latestAppliedFetchSequenceRef.current = requestSequence;
        console.error("Error fetching test rig data:", error);
        setDataLoadError(error?.response?.data?.message || error?.message || 'Failed to load test rig data.');
      }
    } finally {
      if (showLoading && shouldApply) {
        setLoading(false);
      }
    }
  };

  const handleModelChange = (newModel) => {
    setIsChangingModel(true);
    setModelName(newModel);
    setIsChangingModel(false);
  };

  const handleResizeModeChange = (newResizeMode) => {
    setIsChangingResizeMode(true);
    setResizeMode(newResizeMode);
    setIsChangingResizeMode(false);
  };

  const handleCompressionModeChange = (newCompressionMode) => {
    setIsChangingCompressionMode(true);
    setCompressionMode(newCompressionMode);
    setIsChangingCompressionMode(false);
  };

  const handleUseEbayRagChange = (nextValue) => {
    setIsChangingEbayRag(true);
    setUseEbayRag(toBool(nextValue, false));
    setIsChangingEbayRag(false);
  };

  const handleUseNotesRagChange = (nextValue) => {
    setIsChangingNotesRag(true);
    setUseNotesRag(toBool(nextValue, false));
    setIsChangingNotesRag(false);
  };

  const selectDataset = (dataset) => {
    if (!dataset) return;
    setSelectedDataset(dataset);
    setDatasetSearch(dataset.name || dataset.prefix || '');
    setIsDatasetDropdownOpen(false);
  };

  const refreshDatasets = async () => {
    if (isRefreshingDatasets) return;
    setIsRefreshingDatasets(true);
    setDatasetLoadError('');
    try {
      const response = await axios.post('/refresh_test_datasets');
      const nextDatasets = Array.isArray(response?.data?.datasets) ? response.data.datasets : [];
      setDatasets(nextDatasets);
      if (nextDatasets.length === 0) {
        setSelectedDataset(null);
        setDatasetSearch('');
        return;
      }
      const currentPrefix = selectedDataset?.prefix || '';
      const matchingSelection = nextDatasets.find((entry) => (entry.prefix || '') === currentPrefix) || null;
      if (matchingSelection) {
        setSelectedDataset(matchingSelection);
        setDatasetSearch(matchingSelection.name || matchingSelection.prefix || '');
      } else {
        setSelectedDataset(null);
        setDatasetSearch('');
      }
    } catch (error) {
      console.error('Error refreshing test datasets:', error);
      setDatasetLoadError(error?.response?.data?.message || error?.message || 'Failed to refresh datasets.');
    } finally {
      setIsRefreshingDatasets(false);
    }
  };

  const fetchDatasets = async () => {
    setIsLoadingDatasets(true);
    setDatasetLoadError('');
    try {
      const response = await axios.get('/test_datasets');
      const nextDatasets = Array.isArray(response?.data?.datasets) ? response.data.datasets : [];
      setDatasets(nextDatasets);

      if (nextDatasets.length === 0) {
        setSelectedDataset(null);
        setDatasetSearch('');
        return;
      }

      const currentPrefix = selectedDataset?.prefix || '';
      const matchingSelection = nextDatasets.find((entry) => (entry.prefix || '') === currentPrefix) || null;

      if (matchingSelection) {
        setSelectedDataset(matchingSelection);
        setDatasetSearch(matchingSelection.name || matchingSelection.prefix || '');
        return;
      }

      setSelectedDataset(null);
      setDatasetSearch('');
    } catch (error) {
      console.error('Error loading test datasets:', error);
      setDatasets([]);
      setSelectedDataset(null);
      setDatasetSearch('');
      setDatasetLoadError(error?.response?.data?.message || error?.message || 'Failed to load datasets.');
    } finally {
      setIsLoadingDatasets(false);
    }
  };

  const normalizedDatasetSearch = datasetSearch.trim().toLowerCase();
  const filteredDatasets = datasets.filter((dataset) => {
    if (!normalizedDatasetSearch) return true;
    const name = (dataset?.name || '').toLowerCase();
    const prefix = (dataset?.prefix || '').toLowerCase();
    return name.includes(normalizedDatasetSearch) || prefix.includes(normalizedDatasetSearch);
  });

  useEffect(() => {
    fetchData({ showLoading: true });
    fetchDatasets();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datasetDropdownRef.current && !datasetDropdownRef.current.contains(event.target)) {
        setIsDatasetDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (shouldPollRunStatus) {
        fetchData({ showLoading: false });
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [shouldPollRunStatus]);

  const runTestRig = async () => {
    if (isStartingRun) return;
    if (!selectedDataset) {
      alert("Please select a dataset before running the test rig.");
      return;
    }

    setIsStartingRun(true);
    try {
      const response = await axios.post('/run_test_rig', {
        model: modelName,
        resize_mode: resizeMode,
        compression_mode: compressionMode,
        use_ebay_rag: useEbayRag,
        use_notes_rag: useNotesRag,
        dataset_prefix: selectedDataset.prefix || ''
      });
      if (response.data.status === 'success') {
        if (response.data.resize_mode) {
          setResizeMode(response.data.resize_mode);
        }
        if (response.data.compression_mode) {
          setCompressionMode(response.data.compression_mode);
        }
        if (response.data.use_ebay_rag !== undefined) {
          setUseEbayRag(toBool(response.data.use_ebay_rag, useEbayRag));
        }
        if (response.data.use_notes_rag !== undefined) {
          setUseNotesRag(toBool(response.data.use_notes_rag, useNotesRag));
        }
        if (response.data.dataset_prefix !== undefined) {
          const selectedPrefix = response.data.dataset_prefix || '';
          const matchingDataset = datasets.find((entry) => (entry.prefix || '') === selectedPrefix);
          if (matchingDataset) {
            setSelectedDataset(matchingDataset);
            setDatasetSearch(matchingDataset.name || matchingDataset.prefix || '');
          }
        }
        setIsRunning(true);
        const responseActiveRuns = Array.isArray(response?.data?.active_runs) ? response.data.active_runs : [];
        const responseRunContext = (response?.data?.run_context && typeof response.data.run_context === 'object')
          ? response.data.run_context
          : null;
        const optimisticRunContext = (
          responseRunContext || (response?.data?.run_id
            ? {
              run_id: response.data.run_id,
              started_at: response.data.started_at || new Date().toISOString(),
              model: response.data.model || modelName,
              resize_mode: response.data.resize_mode || resizeMode,
              compression_mode: response.data.compression_mode || compressionMode,
              use_ebay_rag: toBool(response.data.use_ebay_rag, useEbayRag),
              use_notes_rag: toBool(response.data.use_notes_rag, useNotesRag),
              dataset_prefix: response.data.dataset_prefix || (selectedDataset?.prefix || ''),
              dataset_name: response.data.dataset_name || selectedDataset?.name || selectedDataset?.prefix || 'N/A',
              total_data_points: response.data.dataset_data_points ?? selectedDataset?.data_points ?? null,
              data_points: response.data.dataset_data_points ?? selectedDataset?.data_points ?? null,
              attempted_data_points: 0,
              successful_data_points: 0,
              skipped_data_points: 0,
              progress_percent: 0,
              phase: 'starting',
            }
            : null)
        );
        const optimisticRuns = normalizeActiveRuns(
          responseActiveRuns.length > 0
            ? responseActiveRuns
            : (optimisticRunContext ? [optimisticRunContext] : [])
        );
        if (optimisticRuns.length > 0) {
          setActiveRuns((previous) => mergeActiveRunsForward(previous, optimisticRuns));
        }
        fetchData({ showLoading: false });
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      if (error.response && error.response.status === 400) {
        const message = error.response?.data?.message || "Unable to start test rig.";
        alert(message);
        if (message.toLowerCase().includes('was not found')) {
          setSelectedDataset(null);
          fetchDatasets();
        }
      } else {
        alert("Error starting test rig: " + (error.response?.data?.message || error.message));
      }
    } finally {
      setIsStartingRun(false);
    }
  };

  const terminateRun = async (runId) => {
    const normalizedRunId = String(runId || '').trim();
    if (!normalizedRunId) {
      alert('Cannot terminate this run because run_id is missing.');
      return;
    }
    if (!window.confirm(`Terminate experiment ${normalizedRunId.slice(0, 8)}?`)) return;

    setTerminatingRunIds((previous) => ({ ...previous, [normalizedRunId]: true }));
    try {
      const response = await axios.post('/terminate_test_rig', { run_id: normalizedRunId });
      if (response?.data?.status === 'success' || response?.data?.status === 'partial_success') {
        fetchData({ showLoading: false });
      } else {
        alert(response?.data?.message || 'Failed to terminate run.');
      }
    } catch (error) {
      const message = error?.response?.data?.message || error.message || 'Failed to terminate run.';
      alert(message);
    } finally {
      setTerminatingRunIds((previous) => {
        const next = { ...previous };
        delete next[normalizedRunId];
        return next;
      });
    }
  };

  const terminateAllRuns = async () => {
    if (!window.confirm("Terminate all currently running experiments?")) return;
    setIsTerminatingAllRuns(true);
    try {
      const response = await axios.post('/terminate_test_rig', { terminate_all: true });
      if (response?.data?.status === 'success' || response?.data?.status === 'partial_success') {
        fetchData({ showLoading: false });
      } else {
        alert(response?.data?.message || 'Failed to terminate active experiments.');
      }
    } catch (error) {
      const message = error?.response?.data?.message || error.message || 'Failed to terminate active experiments.';
      alert(message);
    } finally {
      setIsTerminatingAllRuns(false);
    }
  };

  const flushResults = async () => {
    if (!window.confirm("Are you sure you want to clear all test results? This cannot be undone.")) return;
    if (isRunInProgress) {
        alert("Cannot flush results while test rig is running.");
        return;
    }
    
    setIsFlushing(true);
    try {
      const response = await axios.post('/flush_test_results');
      if (response.data.status === 'success') {
        setTestResults([]);
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error("Error flushing results:", error);
      alert("Failed to flush results.");
    } finally {
      setIsFlushing(false);
    }
  };

  const updateRunLotResultInState = (runId, lotNumber, updater) => {
    const normalizedRunId = String(runId || '').trim();
    const normalizedLot = String(lotNumber || '').trim();
    if (!normalizedRunId || !normalizedLot || typeof updater !== 'function') return;

    setTestResults((previousRuns) => previousRuns.map((run) => {
      if (String(run?.run_id || '').trim() !== normalizedRunId) return run;
      if (!Array.isArray(run?.results)) return run;

      let didChange = false;
      const nextResults = run.results.map((resultEntry) => {
        const resultLot = getResultLotNumber(resultEntry);
        if (resultLot !== normalizedLot) return resultEntry;
        const updated = updater(resultEntry);
        if (updated !== resultEntry) {
          didChange = true;
          return updated;
        }
        return resultEntry;
      });

      if (!didChange) return run;
      return { ...run, results: nextResults };
    }));
  };
  const mergeUpdatedRunInState = (updatedRun) => {
    const normalizedRunId = String(updatedRun?.run_id || '').trim();
    if (!normalizedRunId) return;
    setTestResults((previousRuns) => previousRuns.map((run) => {
      if (String(run?.run_id || '').trim() !== normalizedRunId) return run;
      return {
        ...run,
        ...updatedRun,
        results: Array.isArray(updatedRun?.results) ? updatedRun.results : run.results,
      };
    }));
  };
  const saveReviewNote = async ({ runId, lotNumber, resultIndex, itemIndex, historyIndex, reviewNote }) => {
    const normalizedRunId = String(runId || '').trim();
    const normalizedLot = String(lotNumber || '').trim();
    const normalizedResultIndex = Number(resultIndex);
    const normalizedItemIndex = Number(itemIndex);
    const normalizedHistoryIndex = historyIndex != null ? Number(historyIndex) : null;

    if (!normalizedRunId || !normalizedLot) {
      alert('Missing run_id or lot_number for review note update.');
      return;
    }
    if (!Number.isFinite(normalizedResultIndex) || normalizedResultIndex < 0) {
      alert('Missing valid result index for review note update.');
      return;
    }
    if (!Number.isFinite(normalizedItemIndex) || normalizedItemIndex < 0) {
      alert('Missing valid item index for review note update.');
      return;
    }

    const noteDraftKey = getReviewNoteDraftKey({
      runId: normalizedRunId,
      lotNumber: normalizedLot,
      resultIndex: normalizedResultIndex,
      itemIndex: normalizedItemIndex,
      historyIndex: normalizedHistoryIndex,
    });
    const noteText = String(reviewNote || '').slice(0, 4000);

    setSavingReviewNotes((previous) => ({ ...previous, [noteDraftKey]: true }));
    try {
      const payload = {
        run_id: normalizedRunId,
        lot_number: normalizedLot,
        result_index: normalizedResultIndex,
        item_index: normalizedItemIndex,
        review_note: noteText,
      };
      if (normalizedHistoryIndex != null) {
        payload.history_index = normalizedHistoryIndex;
      }
      const response = await axios.post('/test_rig_update_item_note', payload);

      if (response?.data?.status === 'success') {
        const returnedRun = response?.data?.run;
        const returnedResult = response?.data?.result;
        if (returnedRun && typeof returnedRun === 'object') {
          mergeUpdatedRunInState(returnedRun);
        } else if (returnedResult && typeof returnedResult === 'object') {
          updateRunLotResultInState(normalizedRunId, normalizedLot, () => returnedResult);
        } else {
          fetchData({ showLoading: false });
        }
        setReviewNoteDrafts((previous) => {
          const next = { ...previous };
          delete next[noteDraftKey];
          return next;
        });
      } else {
        alert(response?.data?.message || 'Failed to save reviewer note.');
      }
    } catch (error) {
      const message = error?.response?.data?.message || error.message || 'Failed to save reviewer note.';
      alert(message);
    } finally {
      setSavingReviewNotes((previous) => {
        const next = { ...previous };
        delete next[noteDraftKey];
        return next;
      });
    }
  };
  const saveLotReviewNote = async ({ runId, lotNumber, resultIndex, historyIndex, reviewNote }) => {
    const normalizedRunId = String(runId || '').trim();
    const normalizedLot = String(lotNumber || '').trim();
    const normalizedResultIndex = Number(resultIndex);
    const normalizedHistoryIndex = historyIndex != null ? Number(historyIndex) : null;

    if (!normalizedRunId || !normalizedLot) {
      alert('Missing run_id or lot_number for lot note update.');
      return;
    }
    if (!Number.isFinite(normalizedResultIndex) || normalizedResultIndex < 0) {
      alert('Missing valid result index for lot note update.');
      return;
    }

    const draftKey = `lot-${normalizedRunId}-${normalizedLot}-${normalizedResultIndex}${normalizedHistoryIndex != null ? `-h${normalizedHistoryIndex}` : ''}`;
    const noteText = String(reviewNote || '').slice(0, 4000);

    setSavingLotNotes((prev) => ({ ...prev, [draftKey]: true }));
    try {
      const payload = {
        run_id: normalizedRunId,
        lot_number: normalizedLot,
        result_index: normalizedResultIndex,
        review_note: noteText,
      };
      if (normalizedHistoryIndex != null) {
        payload.history_index = normalizedHistoryIndex;
      }
      const response = await axios.post('/test_rig_update_lot_note', payload);

      if (response?.data?.status === 'success') {
        const returnedRun = response?.data?.run;
        const returnedResult = response?.data?.result;
        if (returnedRun && typeof returnedRun === 'object') {
          mergeUpdatedRunInState(returnedRun);
        } else if (returnedResult && typeof returnedResult === 'object') {
          updateRunLotResultInState(normalizedRunId, normalizedLot, () => returnedResult);
        } else {
          fetchData({ showLoading: false });
        }
        setLotNoteDrafts((prev) => {
          const next = { ...prev };
          delete next[draftKey];
          return next;
        });
      } else {
        alert(response?.data?.message || 'Failed to save lot note.');
      }
    } catch (error) {
      const message = error?.response?.data?.message || error.message || 'Failed to save lot note.';
      alert(message);
    } finally {
      setSavingLotNotes((prev) => {
        const next = { ...prev };
        delete next[draftKey];
        return next;
      });
    }
  };
  const toggleLotImageDisabled = async ({ runId, lotNumber, imageUrl, disable }) => {
    const normalizedRunId = String(runId || '').trim();
    const normalizedLot = String(lotNumber || '').trim();
    const normalizedImage = String(imageUrl || '').trim();
    if (!normalizedRunId || !normalizedLot || !normalizedImage) {
      alert('Missing run, lot, or image identifier.');
      return;
    }

    const actionKey = getImageActionKey(normalizedRunId, normalizedLot, normalizedImage);
    const normalizedImgKey = normalizeImageKey(normalizedImage);
    updateRunLotResultInState(normalizedRunId, normalizedLot, (resultEntry) => {
      const nextDisabled = new Set(getDisabledImageKeySet(resultEntry));
      if (disable) {
        nextDisabled.add(normalizedImgKey);
      } else {
        nextDisabled.delete(normalizedImgKey);
      }
      return {
        ...resultEntry,
        disabled_image_keys: Array.from(nextDisabled).sort(),
      };
    });

    setLotImageActionBusy((previous) => ({ ...previous, [actionKey]: true }));
    try {
      const response = await axios.post('/test_rig_toggle_lot_image', {
        run_id: normalizedRunId,
        lot_number: normalizedLot,
        image_url: normalizedImage,
        disable: !!disable,
      });
      if (response?.data?.status === 'success') {
        const serverDisabledKeys = (
          Array.isArray(response?.data?.disabled_image_keys)
            ? response.data.disabled_image_keys
            : (Array.isArray(response?.data?.result?.disabled_image_keys)
              ? response.data.result.disabled_image_keys
              : null)
        );
        if (serverDisabledKeys) {
          updateRunLotResultInState(normalizedRunId, normalizedLot, (resultEntry) => ({
            ...resultEntry,
            disabled_image_keys: serverDisabledKeys,
          }));
        }
      } else {
        alert(response?.data?.message || 'Failed to update lot image state.');
        fetchData({ showLoading: false });
      }
    } catch (error) {
      const message = error?.response?.data?.message || error.message || 'Failed to update lot image state.';
      alert(message);
      fetchData({ showLoading: false });
    } finally {
      setLotImageActionBusy((previous) => {
        const next = { ...previous };
        delete next[actionKey];
        return next;
      });
    }
  };
  const rerunSingleLot = async ({ runId, lotNumber }) => {
    const getLotRerunSnapshot = (runs, targetRunId, targetLotNumber) => {
      if (!Array.isArray(runs)) return null;
      const targetRun = String(targetRunId || '').trim();
      const targetLot = String(targetLotNumber || '').trim();
      if (!targetRun || !targetLot) return null;

      const runEntry = runs.find((entry) => String(entry?.run_id || '').trim() === targetRun);
      if (!runEntry || !Array.isArray(runEntry.results)) return null;

      const lotEntry = runEntry.results.find((resultEntry) => (
        String(getResultLotNumber(resultEntry) || '').trim() === targetLot
      ));
      if (!lotEntry || typeof lotEntry !== 'object') return null;

      return {
        attemptsUsed: toFiniteCount(lotEntry?.attempts_used) ?? 1,
        rerunHistoryCount: Array.isArray(lotEntry?.rerun_history) ? lotEntry.rerun_history.length : 0,
      };
    };

    const hasRerunSnapshotAdvanced = (beforeSnapshot, currentSnapshot) => {
      if (!currentSnapshot) return false;
      if (!beforeSnapshot) return true;
      return (
        currentSnapshot.attemptsUsed > beforeSnapshot.attemptsUsed
        || currentSnapshot.rerunHistoryCount > beforeSnapshot.rerunHistoryCount
      );
    };

    const waitForRerunAfterGatewayTimeout = async ({
      targetRunId,
      targetLotNumber,
      beforeSnapshot,
    }) => {
      const deadline = Date.now() + RERUN_TIMEOUT_POLL_WINDOW_MS;
      while (Date.now() < deadline) {
        await new Promise((resolve) => {
          setTimeout(resolve, RERUN_TIMEOUT_POLL_INTERVAL_MS);
        });
        try {
          const resultResponse = await axios.get('/get_test_results');
          const nextRuns = Array.isArray(resultResponse?.data) ? resultResponse.data : [];
          const nextSnapshot = getLotRerunSnapshot(nextRuns, targetRunId, targetLotNumber);
          if (hasRerunSnapshotAdvanced(beforeSnapshot, nextSnapshot)) {
            setTestResults(nextRuns);
            return true;
          }
        } catch (pollError) {
          console.warn('Rerun timeout polling failed:', pollError);
        }
      }
      return false;
    };

    const normalizedRunId = String(runId || '').trim();
    const normalizedLot = String(lotNumber || '').trim();
    if (!normalizedRunId || !normalizedLot) {
      alert('Missing run or lot identifier.');
      return;
    }
    if (!window.confirm(`Rerun lot ${normalizedLot} in run ${normalizedRunId.slice(0, 8)}?`)) return;

    const baselineSnapshot = getLotRerunSnapshot(testResults, normalizedRunId, normalizedLot);
    const actionKey = getLotActionKey(normalizedRunId, normalizedLot);
    setRerunningLotBusy((previous) => ({ ...previous, [actionKey]: true }));
    try {
      const response = await axios.post('/rerun_test_rig_lot', {
        run_id: normalizedRunId,
        lot_number: normalizedLot,
      }, { timeout: 300000 });
      if (response?.data?.status === 'success') {
        const returnedRun = response?.data?.run;
        const returnedResult = response?.data?.result;
        if (returnedRun && typeof returnedRun === 'object') {
          mergeUpdatedRunInState(returnedRun);
        } else if (returnedResult && typeof returnedResult === 'object') {
          updateRunLotResultInState(normalizedRunId, normalizedLot, () => returnedResult);
        }
        fetchData({ showLoading: false });
      } else {
        alert(response?.data?.message || 'Failed to rerun lot.');
        fetchData({ showLoading: false });
      }
    } catch (error) {
      const statusCode = Number(error?.response?.status || 0);
      if (statusCode === 504) {
        const completedAfterTimeout = await waitForRerunAfterGatewayTimeout({
          targetRunId: normalizedRunId,
          targetLotNumber: normalizedLot,
          beforeSnapshot: baselineSnapshot,
        });
        if (!completedAfterTimeout) {
          alert(
            "Rerun request hit gateway timeout (504). It may still complete in background. "
            + "If this repeats, increase reverse-proxy and Gunicorn timeouts."
          );
        }
      } else {
        const message = error?.response?.data?.message || error.message || 'Failed to rerun lot.';
        alert(message);
      }
      fetchData({ showLoading: false });
    } finally {
      setRerunningLotBusy((previous) => {
        const next = { ...previous };
        delete next[actionKey];
        return next;
      });
    }
  };

  const formatCostDisplay = (value) => {
    const numericCost = toFiniteNumber(value);
    if (numericCost === null) return 'N/A';
    return `$${numericCost.toFixed(4)}`;
  };

  const stats = {
    avgSpeed: testResults.length > 0 
      ? (testResults.reduce((acc, curr) => acc + (curr.avg_speed_ms || curr.avg_speed || 0), 0) / testResults.length).toFixed(0) 
      : '-',
    avgAccuracy: testResults.length > 0 
      ? (testResults.reduce((acc, curr) => acc + (curr.avg_accuracy || 0), 0) / testResults.length * 100).toFixed(1) + '%' 
      : '-',
    totalCost: (() => {
      if (testResults.length === 0) return '-';
      const knownCosts = testResults
        .map((entry) => toFiniteNumber(entry?.total_cost))
        .filter((value) => value !== null);
      if (knownCosts.length === 0) return 'N/A';
      return knownCosts.reduce((acc, value) => acc + value, 0).toFixed(4);
    })(),
    totalRuns: testResults.length
  };

  const displayedTestResults = [...testResults].sort((a, b) => {
    const ta = new Date(a?.timestamp || 0).getTime();
    const tb = new Date(b?.timestamp || 0).getTime();
    const safeA = Number.isFinite(ta) ? ta : 0;
    const safeB = Number.isFinite(tb) ? tb : 0;
    if (safeA !== safeB) return safeB - safeA;
    return String(b?.run_id || '').localeCompare(String(a?.run_id || ''));
  });
  const normalizedRunModelSearchQuery = runModelSearchQuery.trim().toLowerCase();
  const normalizedRunDatasetSearchQuery = runDatasetSearchQuery.trim().toLowerCase();
  const runMatchesSearch = (run = {}) => {
    const model = String(run?.model || '').toLowerCase();
    const datasetName = String(run?.dataset_name || '').toLowerCase();
    const datasetPrefix = String(run?.dataset_prefix || '').toLowerCase();
    const modelMatches = !normalizedRunModelSearchQuery || model.includes(normalizedRunModelSearchQuery);
    const datasetMatches = (
      !normalizedRunDatasetSearchQuery ||
      datasetName.includes(normalizedRunDatasetSearchQuery) ||
      datasetPrefix.includes(normalizedRunDatasetSearchQuery)
    );
    return modelMatches && datasetMatches;
  };
  const filteredActiveRuns = activeRuns.filter(runMatchesSearch);
  const activeRunIdSet = new Set(
    activeRuns
      .map((run) => String(run?.run_id || '').trim())
      .filter(Boolean)
  );
  const filteredDisplayedTestResults = displayedTestResults.filter((run) => {
    if (!runMatchesSearch(run)) return false;
    const runId = String(run?.run_id || '').trim();
    if (!runId) return true;
    return !activeRunIdSet.has(runId);
  });
  const totalFilteredTestRuns = filteredDisplayedTestResults.length;
  const totalPages = Math.max(1, Math.ceil(totalFilteredTestRuns / itemsPerPage));
  const indexOfLastRun = currentPage * itemsPerPage;
  const indexOfFirstRun = indexOfLastRun - itemsPerPage;
  const pagedDisplayedTestResults = filteredDisplayedTestResults.slice(indexOfFirstRun, indexOfLastRun);
  const showingFirstRunNumber = totalFilteredTestRuns > 0 ? indexOfFirstRun + 1 : 0;
  const showingLastRunNumber = totalFilteredTestRuns > 0
    ? Math.min(indexOfLastRun, totalFilteredTestRuns)
    : 0;
  const paginate = (pageNumber) => {
    const nextPage = Math.max(1, Math.min(totalPages, pageNumber));
    setCurrentPage(nextPage);
    setExpandedRun(null);
  };
  const runSearchSummary = [
    normalizedRunModelSearchQuery ? `model "${runModelSearchQuery.trim()}"` : '',
    normalizedRunDatasetSearchQuery ? `dataset "${runDatasetSearchQuery.trim()}"` : '',
  ].filter(Boolean).join(' and ');
  const primaryActiveRun = activeRuns.length > 0 ? activeRuns[0] : null;
  const primaryRunProgress = primaryActiveRun ? getRunProgressMetrics(primaryActiveRun) : null;
  const codePromptTemplate = (
    useEbayRag
      ? (useNotesRag ? codePromptTemplates.ebay_on_notes_on : codePromptTemplates.ebay_on_notes_off)
      : (useNotesRag ? codePromptTemplates.ebay_off_notes_on : codePromptTemplates.ebay_off_notes_off)
  );
  const visiblePromptTemplate = withRagPromptPreview(
    codePromptTemplate || '',
    useEbayRag,
    useNotesRag
  );
  const datasetBadgeText = selectedDataset
    ? `${selectedDataset.name}${selectedDataset.data_points !== undefined ? ` (${selectedDataset.data_points} data points)` : ''}`
    : (
      isLoadingDatasets
        ? 'Loading datasets...'
        : (datasetLoadError
          ? 'Dataset load failed'
          : (datasets.length > 0 ? 'Select a dataset to run' : 'No datasets available'))
    );

  useEffect(() => {
    setCurrentPage(1);
  }, [runModelSearchQuery, runDatasetSearchQuery, itemsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
      setExpandedRun(null);
    }
  }, [currentPage, totalPages]);

  return (
    <div className="w-full max-w-none p-3 sm:p-6 bg-white rounded-lg shadow-md">
      <div className="flex flex-col gap-3 mb-6 sm:mb-8">
        {/* Row 1: Title + Dataset badge */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Test Rig</h1>
          <div className="flex items-center px-3 sm:px-4 py-2 bg-blue-50 text-blue-700 rounded border border-blue-100">
            <BarChart2 size={18} className="mr-2 flex-shrink-0" />
            <span className="text-xs sm:text-sm font-medium">
              Dataset: {datasetBadgeText}
            </span>
          </div>
        </div>
        {/* Row 2: Dropdowns */}
        <div className="flex flex-col md:flex-row md:items-stretch md:flex-nowrap gap-2">
          <div ref={datasetDropdownRef} className="relative md:flex-[2.4]">
            <div className="flex items-center bg-slate-50 p-2 rounded-lg border border-slate-200 h-full">
              <span className="text-sm font-medium text-slate-700 mr-2">Dataset:</span>
              <input
                value={datasetSearch}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  const normalized = nextValue.trim().toLowerCase();
                  const exactMatch = datasets.find((dataset) => {
                    const datasetName = (dataset?.name || '').trim().toLowerCase();
                    const datasetPrefix = (dataset?.prefix || '').trim().toLowerCase();
                    return normalized && (datasetName === normalized || datasetPrefix === normalized);
                  }) || null;
                  setDatasetSearch(nextValue);
                  setSelectedDataset(exactMatch);
                  setIsDatasetDropdownOpen(true);
                }}
                onFocus={() => setIsDatasetDropdownOpen(true)}
                disabled={isLoadingDatasets}
                placeholder={isLoadingDatasets ? 'Loading datasets...' : 'Search datasets...'}
                className={`w-full bg-white border border-slate-300 rounded px-2 py-1 outline-none focus:border-blue-500 text-xs font-medium ${
                  isLoadingDatasets ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              />
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => setIsDatasetDropdownOpen((previous) => !previous)}
                className="ml-2 inline-flex items-center justify-center rounded border border-slate-300 bg-white px-1.5 py-1 text-slate-600 hover:bg-slate-100"
                title={isDatasetDropdownOpen ? 'Close dataset list' : 'Open dataset list'}
                aria-label={isDatasetDropdownOpen ? 'Close dataset list' : 'Open dataset list'}
              >
                <ChevronDown size={14} className={`transition-transform ${isDatasetDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => refreshDatasets()}
                disabled={isRefreshingDatasets}
                className={`ml-1 inline-flex items-center justify-center rounded border px-1.5 py-1 transition-colors ${
                  isRefreshingDatasets
                    ? 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                    : 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
                }`}
                title="Repull dataset list from blob storage"
                aria-label="Repull dataset list from blob storage"
              >
                <RefreshCcw size={14} className={isRefreshingDatasets ? 'animate-spin' : ''} />
              </button>
            </div>
            {isDatasetDropdownOpen && (
              <div className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-xl">
                {isLoadingDatasets ? (
                  <div className="px-3 py-2 text-xs text-slate-500">Loading datasets...</div>
                ) : filteredDatasets.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-slate-500">
                    {datasetLoadError
                      ? `Failed to load datasets: ${datasetLoadError}`
                      : (datasets.length > 0 ? 'No matching datasets.' : 'No datasets available.')}
                  </div>
                ) : (
                  filteredDatasets.map((dataset) => (
                    <button
                      key={dataset.id || dataset.prefix || '__root__'}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectDataset(dataset);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs border-b border-slate-100 last:border-b-0 hover:bg-slate-50 ${
                        (selectedDataset?.prefix || '') === (dataset.prefix || '') ? 'bg-blue-50 text-blue-700' : 'text-slate-700'
                      }`}
                    >
                      <div className="font-semibold">{dataset.name}</div>
                      <div className="text-[11px] text-slate-500">
                        {(dataset.prefix || '__root__')} • {dataset.data_points || 0} data points • {dataset.image_count || 0} images
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <div className="flex items-center bg-slate-50 p-2 rounded-lg border border-slate-200 md:flex-[1.6]">
            <Settings size={18} className="text-slate-500 mr-2" />
            <span className="text-sm font-medium text-slate-700 mr-2">Model:</span>
            <select 
              value={modelName}
              onChange={(e) => handleModelChange(e.target.value)}
              disabled={isChangingModel}
              className={`bg-white border border-slate-300 rounded px-2 py-1 outline-none focus:border-blue-500 text-xs font-medium w-full ${
                isChangingModel ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
              }`}
            >
              <optgroup label="GPT-5 Series (Latest)">
                <option value="gpt-5.4">gpt-5.4</option>
                <option value="gpt-5.4-mini">gpt-5.4-mini</option>
                <option value="gpt-5.2">gpt-5.2</option>
                <option value="gpt-5.1">gpt-5.1</option>
                <option value="gpt-5">gpt-5</option>
              </optgroup>
              <optgroup label="Gemini">
                <option value="gemini-flash-latest">gemini-flash-latest</option>
                <option value="gemini-2.5-pro">gemini-2.5-pro</option>
                <option value="gemini-2.5-flash">gemini-2.5-flash</option>
                <option value="gemini-2.0-flash">gemini-2.0-flash</option>
              </optgroup>
              <optgroup label="Claude">
                <option value="claude-sonnet-4-5">claude-sonnet-4-5</option>
                <option value="claude-haiku-4-5">claude-haiku-4-5</option>
                <option value="claude-opus-4-6">claude-opus-4-6</option>
                <option value="claude-opus-4-7">claude-opus-4-7</option>
              </optgroup>
              <optgroup label="GPT-4 Series">
                <option value="gpt-4o">gpt-4o</option>
                <option value="gpt-4o-mini">gpt-4o-mini</option>
                <option value="gpt-4.1">gpt-4.1</option>
                <option value="gpt-4.1-mini">gpt-4.1-mini</option>
              </optgroup>
              <optgroup label="Reasoning Models">
                <option value="o1">o1</option>
                <option value="o3-mini">o3-mini</option>
                <option value="o3">o3</option>
                <option value="o4-mini">o4-mini</option>
              </optgroup>
            </select>
            {isChangingModel && <span className="text-xs text-slate-400 ml-2">Updating...</span>}
          </div>
          <div className="flex items-center bg-slate-50 p-2 rounded-lg border border-slate-200 md:flex-[1.2]">
            <span className="text-sm font-medium text-slate-700 mr-2">Resize:</span>
            <select
              value={resizeMode}
              onChange={(e) => handleResizeModeChange(e.target.value)}
              disabled={isChangingResizeMode}
              className={`bg-white border border-slate-300 rounded px-2 py-1 outline-none focus:border-blue-500 text-xs font-medium w-full ${
                isChangingResizeMode ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
              }`}
            >
              <option value="original">None</option>
              <option value="1600">Max 1600px</option>
              <option value="1280">Max 1280px</option>
              <option value="1024">Max 1024px</option>
              <option value="768">Max 768px</option>
              <option value="512">Max 512px</option>
              <option value="128">Max 128px</option>
            </select>
            {isChangingResizeMode && <span className="text-xs text-slate-400 ml-2">Updating...</span>}
          </div>
          <div className="flex items-center bg-slate-50 p-2 rounded-lg border border-slate-200 md:flex-[1.2]">
            <span className="text-sm font-medium text-slate-700 mr-2">Compression:</span>
            <select
              value={compressionMode}
              onChange={(e) => handleCompressionModeChange(e.target.value)}
              disabled={isChangingCompressionMode}
              className={`bg-white border border-slate-300 rounded px-2 py-1 outline-none focus:border-blue-500 text-xs font-medium w-full ${
                isChangingCompressionMode ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
              }`}
            >
              <option value="none">None</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
            {isChangingCompressionMode && <span className="text-xs text-slate-400 ml-2">Updating...</span>}
          </div>
          <div className="flex items-center bg-slate-50 p-2 rounded-lg border border-slate-200 md:flex-[1.2]">
            <span className="text-sm font-medium text-slate-700 mr-2 whitespace-nowrap">eBay RAG:</span>
            <button
              type="button"
              onClick={() => handleUseEbayRagChange(!useEbayRag)}
              disabled={isChangingEbayRag}
              className={`w-full rounded px-2 py-1 text-xs font-medium border transition-colors ${
                useEbayRag
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-slate-300 bg-white text-slate-600'
              } ${isChangingEbayRag ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'}`}
            >
              {isChangingEbayRag ? 'Updating...' : (useEbayRag ? 'Enabled' : 'Disabled')}
            </button>
          </div>
          <div className="flex items-center bg-slate-50 p-2 rounded-lg border border-slate-200 md:flex-[1.2]">
            <span className="text-sm font-medium text-slate-700 mr-2 whitespace-nowrap">Notes RAG:</span>
            <button
              type="button"
              onClick={() => handleUseNotesRagChange(!useNotesRag)}
              disabled={isChangingNotesRag}
              className={`w-full rounded px-2 py-1 text-xs font-medium border transition-colors ${
                useNotesRag
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                  : 'border-slate-300 bg-white text-slate-600'
              } ${isChangingNotesRag ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'}`}
            >
              {isChangingNotesRag ? 'Updating...' : (useNotesRag ? 'Enabled' : 'Disabled')}
            </button>
          </div>
        </div>
        {/* Row 3: Action buttons */}
        <div className="flex items-center gap-2 sm:gap-3 md:justify-end">
            <button 
              onClick={runTestRig}
              disabled={isStartingRun || isRunning === null || !selectedDataset}
              className={`flex items-center flex-1 sm:flex-none justify-center px-4 py-2 rounded text-white font-medium transition-colors ${
                isStartingRun || isRunning === null || !selectedDataset ? 'bg-slate-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              <Play size={18} className="mr-1.5" />
              <span className="text-sm">{isStartingRun ? 'Starting...' : isRunning === null ? 'Checking...' : 'Run Test'}</span>
            </button>
            <button 
              onClick={() => {
                fetchData();
                fetchDatasets();
              }}
              className="flex items-center px-3 py-2 bg-slate-100 text-slate-700 rounded hover:bg-slate-200 transition-colors"
              title="Refresh data"
            >
              <RefreshCcw size={18} className={loading ? 'animate-spin' : ''} />
              <span className="text-sm ml-1.5 hidden sm:inline">Refresh</span>
            </button>
            <button 
              onClick={flushResults}
              disabled={isFlushing || isRunInProgress || isRunning === null || testResults.length === 0}
              className={`flex items-center px-3 py-2 rounded text-white transition-colors ${
                isFlushing || isRunInProgress || isRunning === null || testResults.length === 0 ? 'bg-slate-400 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'
              }`}
              title="Clear all test results"
            >
              <Trash2 size={18} className={isFlushing ? 'animate-spin' : ''} />
              <span className="text-sm ml-1.5 hidden sm:inline">{isFlushing ? 'Flushing...' : 'Flush'}</span>
            </button>
        </div>
        <div className="bg-slate-900 rounded-lg border border-slate-700 p-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-300 mb-2">
            Prompt Template
          </div>
          <pre className="m-0 text-[11px] leading-relaxed whitespace-pre-wrap break-words text-slate-100 font-mono max-h-48 overflow-y-auto">
            {loading
              ? 'Loading prompt template...'
              : (visiblePromptTemplate || 'No prompt template available from backend code.')}
          </pre>
        </div>
        {loading && activeRuns.length === 0 && (
          <div className="bg-slate-50 rounded-lg border border-slate-200 p-3 text-xs text-slate-500">
            Checking for running experiments...
          </div>
        )}
        {activeRuns.length > 0 && (
          <div className="bg-blue-50 rounded-lg border border-blue-200 p-3">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                Running Experiments ({activeRuns.length})
              </div>
              <button
                type="button"
                onClick={terminateAllRuns}
                disabled={isTerminatingAllRuns}
                className={`inline-flex items-center px-2.5 py-1.5 rounded text-xs font-semibold text-white transition-colors ${
                  isTerminatingAllRuns ? 'bg-slate-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                <Square size={13} className="mr-1.5" />
                {isTerminatingAllRuns ? 'Terminating...' : 'Terminate All'}
              </button>
            </div>
            <div className="space-y-2">
              {activeRuns.map((run, idx) => {
                const runKey = run.run_id || String(run.pid || idx);
                const isTerminatingRun = !!terminatingRunIds[run.run_id];
                const progress = getRunProgressMetrics(run);
                const processedLabel = progress.total !== null
                  ? `${progress.attempted}/${progress.total}${progress.datasetTruncated ? ' (truncated)' : ''}`
                  : `${progress.attempted}/?`;
                return (
                  <div key={runKey} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-white border border-blue-100 rounded p-2.5">
                    <div className="text-xs text-slate-700 flex-1 min-w-0">
                      <div className="font-semibold">
                        {(run.dataset_name || run.dataset_prefix || 'Container root')} • {run.model || 'N/A'}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {formatStartedAt(run.started_at)} • resize {formatResizeDisplay(run.resize_mode)} • compression {run.compression_mode || 'none'} • eBay RAG {run.use_ebay_rag ? 'on' : 'off'} • Notes RAG {run.use_notes_rag ? 'on' : 'off'}
                      </div>
                      <div className="mt-1 text-[11px] text-blue-800">
                        Progress {processedLabel} ({progress.progressPercent.toFixed(1)}%) • success {progress.successful} • skipped {progress.skipped} • ETA {progress.attempted > 0 ? formatEta(progress.etaSeconds) : 'N/A'}
                        {progress.currentLot ? ` • lot #${progress.currentLot}` : ''}
                        {progress.currentAttempt ? ` • attempt ${progress.currentAttempt}` : ''}
                      </div>
                      <div className="mt-1.5 h-1.5 bg-blue-100 rounded overflow-hidden">
                        <div
                          className="h-full bg-blue-500 transition-all duration-500"
                          style={{ width: `${Math.max(0, Math.min(100, progress.progressPercent))}%` }}
                        />
                      </div>
                      {progress.lastError && (
                        <div className="mt-1 text-[11px] text-amber-700 truncate" title={progress.lastError}>
                          Last error: {progress.lastError}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => terminateRun(run.run_id)}
                      disabled={!run.run_id || isTerminatingRun}
                      className={`inline-flex items-center justify-center px-2.5 py-1.5 rounded text-xs font-semibold text-white transition-colors ${
                        !run.run_id || isTerminatingRun ? 'bg-slate-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'
                      }`}
                    >
                      <Square size={13} className="mr-1.5" />
                      {isTerminatingRun ? 'Terminating...' : 'Terminate'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        </div>


      <div className="mb-10 p-10 bg-slate-50 rounded-lg border border-slate-200 text-center">
        <div className="flex flex-col items-center justify-center max-w-lg mx-auto">
          <BarChart2 size={64} className="text-slate-300 mb-4" />
          <h2 className="text-xl font-bold text-slate-700 mb-2">Detailed Analytics Engine</h2>
              <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden">
                <div 
                  className="bg-green-500 h-full transition-all duration-1000" 
                  style={{
                    width: loading
                      ? '35%'
                      : (isRunInProgress
                        ? `${Math.max(2, Math.min(100, primaryRunProgress?.progressPercent ?? 0))}%`
                        : '100%')
                  }}
                ></div>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                {loading
                  ? 'Loading test rig status...'
                  : (isRunInProgress
                    ? `Processing ${primaryRunProgress?.attempted ?? 0}/${primaryRunProgress?.total ?? '?'}${primaryRunProgress?.datasetTruncated ? ' (truncated)' : ''} lots (${(primaryRunProgress?.progressPercent ?? 0).toFixed(1)}%), ETA ${(primaryRunProgress?.attempted ?? 0) > 0 ? formatEta(primaryRunProgress?.etaSeconds) : 'N/A'}`
                    : 'System ready for full dataset pass')}
              </p>
            </div>
          </div>

      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-xl font-semibold text-slate-800">Recent Test Runs</h3>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <input
            value={runModelSearchQuery}
            onChange={(event) => {
              setRunModelSearchQuery(event.target.value);
              setExpandedRun(null);
            }}
            placeholder="Filter by model..."
            className="w-full sm:w-56 bg-white border border-slate-300 rounded px-3 py-1.5 outline-none focus:border-blue-500 text-sm text-slate-700"
          />
          <input
            value={runDatasetSearchQuery}
            onChange={(event) => {
              setRunDatasetSearchQuery(event.target.value);
              setExpandedRun(null);
            }}
            placeholder="Filter by dataset..."
            className="w-full sm:w-56 bg-white border border-slate-300 rounded px-3 py-1.5 outline-none focus:border-blue-500 text-sm text-slate-700"
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1460px] text-left border-collapse">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="p-4 font-semibold text-slate-700"></th>
              <th className="p-4 font-semibold text-slate-700">Timestamp</th>
              <th className="p-4 font-semibold text-slate-700">Model</th>
              <th className="p-4 font-semibold text-slate-700">Resize</th>
              <th className="p-4 font-semibold text-slate-700">Compression</th>
              <th className="p-4 font-semibold text-slate-700">eBay RAG</th>
              <th className="p-4 font-semibold text-slate-700">Notes RAG</th>
              <th className="p-4 font-semibold text-slate-700">Dataset</th>
              <th className="p-4 font-semibold text-slate-700">Mode</th>
              <th className="p-4 font-semibold text-slate-700">Data Points</th>
              <th className="p-4 font-semibold text-slate-700">Avg Speed</th>
              <th className="p-4 font-semibold text-slate-700">Accuracy</th>
              <th className="p-4 font-semibold text-slate-700">Input Tokens</th>
              <th className="p-4 font-semibold text-slate-700">Output Tokens</th>
              <th className="p-4 font-semibold text-slate-700">Cost</th>
            </tr>
          </thead>
          <tbody>
            {filteredActiveRuns.map((runningRun, ridx) => (
              (() => {
                const progress = getRunProgressMetrics(runningRun);
                const processed = progress.total !== null
                  ? `${progress.attempted}/${progress.total}${progress.datasetTruncated ? ' (truncated)' : ''}`
                  : `${progress.attempted}/?`;
                const progressHint = `Success ${progress.successful}, skipped ${progress.skipped}, ETA ${progress.attempted > 0 ? formatEta(progress.etaSeconds) : 'N/A'}`;
                return (
                  <tr
                    key={`running-${runningRun.run_id || runningRun.pid || ridx}`}
                    className="border-b border-blue-100 bg-blue-50/70"
                  >
                    <td className="p-4 text-sm text-blue-700">
                      <RefreshCcw size={16} className="animate-spin" />
                    </td>
                    <td className="p-4 text-sm text-blue-700 font-medium">{formatStartedAt(runningRun.started_at)}</td>
                    <td className="p-4 text-sm text-blue-700">{runningRun.model || 'N/A'}</td>
                    <td className="p-4 text-sm text-blue-700">{formatResizeDisplay(runningRun.resize_mode)}</td>
                    <td className="p-4 text-sm text-blue-700">{runningRun.compression_mode || 'N/A'}</td>
                    <td className="p-4 text-sm text-blue-700">{runningRun.use_ebay_rag ? 'On' : 'Off'}</td>
                    <td className="p-4 text-sm text-blue-700">{runningRun.use_notes_rag ? 'On' : 'Off'}</td>
                    <td className="p-4 text-sm text-blue-700">{runningRun.dataset_name || runningRun.dataset_prefix || 'Container root'}</td>
                    <td className="p-4 text-sm">
                      <span className="px-2 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700">
                        RUNNING {progress.progressPercent.toFixed(0)}%
                      </span>
                    </td>
                    <td className="p-4 text-sm text-blue-700" title={progressHint}>
                      {processed}
                    </td>
                    <td className="p-4 text-sm text-blue-700">
                      {progress.avgDurationSeconds ? `${(progress.avgDurationSeconds * 1000).toFixed(0)} ms` : 'N/A'}
                    </td>
                    <td className="p-4 text-sm text-blue-700">-</td>
                    <td className="p-4 text-sm text-blue-700">-</td>
                    <td className="p-4 text-sm text-blue-700">-</td>
                    <td className="p-4 text-sm text-blue-700">-</td>
                  </tr>
                );
              })()
            ))}
            {pagedDisplayedTestResults.map((run, idx) => {
              const absoluteIdx = indexOfFirstRun + idx;
              const runRowKey = `${String(run?.run_id || 'run')}-${String(run?.timestamp || 'ts')}-${absoluteIdx}`;
              const isExpanded = expandedRun === runRowKey;
              const { attempted, successful, skipped } = getRunPointStats(run);
              const runTokenTotals = getRunTokenTotals(run);
              const runCacheStats = getRunCacheStats(run);
              const runDataPointsHint = getRunDataPointsHint(run);
              const canExportRun = Array.isArray(run?.results) && run.results.length > 0;
              return (
                <React.Fragment key={runRowKey}>
                  <tr className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setExpandedRun(isExpanded ? null : runRowKey)}>
                    <td className="p-4 text-sm text-slate-600">
                      <div className="flex items-center gap-2">
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            exportRunToCsv(run, absoluteIdx);
                          }}
                          disabled={!canExportRun}
                          className={`inline-flex items-center rounded border px-1.5 py-1 text-[11px] font-semibold transition-colors ${
                            canExportRun
                              ? 'border-slate-300 bg-white text-slate-600 hover:bg-slate-100'
                              : 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                          }`}
                          title={canExportRun ? 'Export this test run to CSV' : 'No row-level results to export'}
                          aria-label="Export run to CSV"
                        >
                          <Download size={12} className="mr-1" />
                          CSV
                        </button>
                      </div>
                    </td>
                    <td className="p-4 text-sm text-slate-600">{new Date(run.timestamp).toLocaleString()}</td>
                    <td className="p-4 text-sm text-slate-600">{run.model || 'N/A'}</td>
                    <td className="p-4 text-sm text-slate-600">{formatResizeDisplay(run.resize_mode)}</td>
                    <td className="p-4 text-sm text-slate-600">{run.compression_mode || 'N/A'}</td>
                    <td className="p-4 text-sm text-slate-600">{toBool(run.use_ebay_rag, false) ? 'On' : 'Off'}</td>
                    <td className="p-4 text-sm text-slate-600">{toBool(run.use_notes_rag, false) ? 'On' : 'Off'}</td>
                    <td className="p-4 text-sm text-slate-600">{run.dataset_name || run.dataset_prefix || 'Container root'}</td>
                    <td className="p-4 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                            run.mode === 'MOCK' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
                        }`}>
                            {run.mode || 'UNKNOWN'}
                        </span>
                    </td>
                    <td className="p-4 text-sm text-slate-600" title={runDataPointsHint || undefined}>{formatRunDataPoints(run)}</td>
                    <td className="p-4 text-sm text-slate-600">{(run.avg_speed_ms || run.avg_speed) ? `${(run.avg_speed_ms || run.avg_speed).toFixed(0)} ms` : 'N/A'}</td>
                    <td className="p-4 text-sm font-bold text-slate-700">{run.avg_accuracy ? `${(run.avg_accuracy * 100).toFixed(1)}%` : 'N/A'}</td>
                    <td className="p-4 text-sm text-slate-600">
                      <span className={`inline-flex rounded px-2 py-0.5 text-[11px] font-semibold ${
                        toFiniteCount(runTokenTotals.input) !== null ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {formatTokenCount(runTokenTotals.input)}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-slate-600">
                      <span className={`inline-flex rounded px-2 py-0.5 text-[11px] font-semibold ${
                        toFiniteCount(runTokenTotals.output) !== null ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {formatTokenCount(runTokenTotals.output)}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-slate-600">{formatCostDisplay(run.total_cost)}</td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <td colSpan="15" className="p-4">
                        <div className="space-y-3">
                          <div className={`rounded border p-2 text-xs ${
                            skipped > 0 ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-emerald-50 border-emerald-200 text-emerald-700'
                          }`}>
                            Data points: {successful ?? attempted ?? 0}
                            {attempted !== null ? ` successful out of ${attempted}` : ''}
                            {skipped > 0 ? ` (${skipped} skipped)` : ''}
                          </div>
                          <div className="rounded border border-slate-200 bg-white p-2 text-xs text-slate-700">
                            <span className="mr-2">Tokens:</span>
                            <span className={`inline-flex rounded px-1.5 py-0.5 text-[11px] font-semibold ${
                              toFiniteCount(runTokenTotals.input) !== null ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'
                            }`}>
                              In {formatTokenCount(runTokenTotals.input)}
                            </span>
                            <span className={`inline-flex rounded px-1.5 py-0.5 text-[11px] font-semibold ml-1 ${
                              toFiniteCount(runTokenTotals.output) !== null ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-500'
                            }`}>
                              Out {formatTokenCount(runTokenTotals.output)}
                            </span>
                            <span className={`inline-flex rounded px-1.5 py-0.5 text-[11px] font-semibold ml-1 ${
                              toFiniteCount(runTokenTotals.total) !== null ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                            }`}>
                              Tot {formatTokenCount(runTokenTotals.total)}
                            </span>
                          </div>
                          <div className="rounded border border-cyan-200 bg-cyan-50 p-2 text-xs text-cyan-800">
                            Cache hits: {runCacheStats.count}/{runCacheStats.total}
                            {runCacheStats.total > 0 ? ` (${(runCacheStats.rate * 100).toFixed(1)}%)` : ''}
                          </div>
                          {Array.isArray(run.skipped_lots) && run.skipped_lots.length > 0 && (
                            <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                              Skipped lots: {run.skipped_lots.slice(0, 12).join(', ')}
                              {run.skipped_lots.length > 12 ? `, +${run.skipped_lots.length - 12} more` : ''}
                            </div>
                          )}
                          <div className="bg-slate-900 rounded border border-slate-700 p-3">
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-300 mb-2">
                              Prompt Template
                            </div>
                            <pre className="m-0 text-[11px] leading-relaxed whitespace-pre-wrap break-words text-slate-100 font-mono max-h-64 overflow-y-auto">
                              {withRagPromptPreview(run.prompt_template || '', run.use_ebay_rag, run.use_notes_rag) || 'Prompt template was not stored for this run.'}
                            </pre>
                          </div>
                          <div className="bg-white rounded border border-slate-200 shadow-inner overflow-x-auto">
                            <table className="w-full text-left text-[11px] table-fixed">
                              <thead>
                                <tr className="bg-slate-100">
                                  <th className="px-1.5 py-1 border-r border-b w-[3%]">Lot #</th>
                                  <th className="px-1.5 py-1 border-r border-b w-[7%]">Image</th>
                                  <th className="px-1.5 py-1 border-r border-b w-[12%]">AI Item Name</th>
                                  <th className="px-1.5 py-1 border-r border-b w-[5%]">AI Est.</th>
                                  <th className="px-1.5 py-1 border-r border-b w-[5%]">AI Est. Sum</th>
                                  <th className="px-1.5 py-1 border-r border-b w-[5%]">Actual</th>
                                  <th className="px-1.5 py-1 border-r border-b w-[14%]">AI Explanation</th>
                                  <th className="px-1.5 py-1 border-r border-b w-[10%]">Subitem Notes</th>
                                  <th className="px-1.5 py-1 border-r border-b w-[10%]">Item Notes</th>
                                  <th className="px-1.5 py-1 border-r border-b w-[4.5%]">Acc.</th>
                                  <th className="px-1.5 py-1 border-r border-b w-[4.5%]">Dur.</th>
                                  <th className="px-1.5 py-1 border-r border-b w-[4.5%]">Src</th>
                                  <th className="px-1.5 py-1 border-r border-b w-[5%]">In Tok</th>
                                  <th className="px-1.5 py-1 border-r border-b w-[5%]">Out Tok</th>
                                  <th className="px-1.5 py-1 border-b w-[5%]">Cost</th>
                                </tr>
                              </thead>
                              <tbody>
                                {run.results?.map((res, ridx) => {
                                  const resultItems = Array.isArray(res?.items) && res.items.length > 0 ? res.items : [null];
                                  const itemCount = resultItems.length;
                                  const resultTokenTotals = getResultTokenCounts(res);
                                  const sourceInfo = getResultAttemptSourceInfo(res);
                                  const lotNumber = getResultLotNumber(res, resultItems[0]);
                                  const runId = String(run?.run_id || '').trim();
                                  const lotActionKey = getLotActionKey(runId, lotNumber || ridx);
                                  const isRerunningLot = !!rerunningLotBusy[lotActionKey];
                                  const resultImageUrls = getResultImageUrls(res, resultItems[0]);
                                  const disabledImageKeySet = getDisabledImageKeySet(res);
                                  const activeImageCount = resultImageUrls.reduce((count, url) => {
                                    const imageKey = normalizeImageKey(url);
                                    return disabledImageKeySet.has(imageKey) ? count : count + 1;
                                  }, 0);
                                  const canRerunLot = Boolean(
                                    runId &&
                                    lotNumber &&
                                    resultImageUrls.length > 0 &&
                                    activeImageCount > 0
                                  );
                                  return (
                                  <React.Fragment key={ridx}>
                                    {resultItems.map((item, iidx) => {
                                      const rowItem = item && typeof item === 'object' ? item : {};
                                      const rowLotNumber = getResultLotNumber(res, rowItem) || lotNumber;
                                      const imageUrlsForModal = getResultImageUrls(res, rowItem);
                                      const actualValue = toFiniteNumber(rowItem.actual);
                                      const estimatedValue = toFiniteNumber(rowItem.estimated);
                                      const reviewNoteDraftKey = getReviewNoteDraftKey({
                                        runId,
                                        lotNumber: rowLotNumber || lotNumber || ridx,
                                        resultIndex: ridx,
                                        itemIndex: iidx,
                                      });
                                      const persistedReviewNote = getTrustedReviewNote(rowItem);
                                      const reviewNoteValue = Object.prototype.hasOwnProperty.call(reviewNoteDrafts, reviewNoteDraftKey)
                                        ? reviewNoteDrafts[reviewNoteDraftKey]
                                        : persistedReviewNote;
                                      const isSavingReviewNote = !!savingReviewNotes[reviewNoteDraftKey];
                                      const hasReviewNoteChanges = reviewNoteValue !== persistedReviewNote;
                                      const canSaveReviewNote = Boolean(runId && (rowLotNumber || lotNumber));
                                      return (
                                      <tr key={`${ridx}-${iidx}`} className={`${iidx < itemCount - 1 ? '' : 'border-b'} hover:bg-slate-50`}>
                                        {iidx === 0 && (
                                          <td className="px-1.5 py-1 border-r align-top" rowSpan={itemCount}>
                                            <span>{rowLotNumber || '-'}</span>
                                          </td>
                                        )}
                                        {iidx === 0 && (
                                          <td className="px-1.5 py-1 border-r align-top" rowSpan={itemCount}>
                                            <div className="flex flex-wrap gap-1">
                                              {resultImageUrls.map((url, uidx) => {
                                                const imageKey = normalizeImageKey(url);
                                                const isDisabledImage = disabledImageKeySet.has(imageKey);
                                                const imageActionKey = getImageActionKey(runId, rowLotNumber || lotNumber, url);
                                                const isImageActionBusy = !!lotImageActionBusy[imageActionKey];
                                                const disablingLastActiveImage = !isDisabledImage && activeImageCount <= 1;
                                                const canToggleImage = Boolean(runId && (rowLotNumber || lotNumber) && imageKey);
                                                return (
                                                  <div key={uidx} className="flex flex-col items-center gap-0.5">
                                                    <div className="relative">
                                                      <img
                                                        src={url}
                                                        alt={`${rowItem.item || rowItem.item_name || 'Lot image'} ${uidx + 1}`}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setSelectedImage(url);
                                                          setSelectedImages(imageUrlsForModal);
                                                          setSelectedLotNum(rowLotNumber);
                                                          setSelectedItems(res.items);
                                                        }}
                                                        className={`w-10 h-10 object-cover rounded shadow-sm hover:scale-[1.5] hover:z-10 transition-transform cursor-pointer ring-2 ${
                                                          isDisabledImage ? 'grayscale opacity-35 ring-red-400' : 'ring-emerald-400'
                                                        }`}
                                                      />
                                                      {isDisabledImage && (
                                                        <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-white bg-slate-900/55 rounded">
                                                          OFF
                                                        </span>
                                                      )}
                                                    </div>
                                                    <button
                                                      type="button"
                                                      onClick={(event) => {
                                                        event.stopPropagation();
                                                        if (!canToggleImage) return;
                                                        toggleLotImageDisabled({
                                                          runId,
                                                          lotNumber: rowLotNumber || lotNumber,
                                                          imageUrl: url,
                                                          disable: !isDisabledImage,
                                                        });
                                                      }}
                                                      disabled={!canToggleImage || isImageActionBusy || isRerunningLot || disablingLastActiveImage}
                                                      className={`rounded border px-1 py-0.5 text-[9px] leading-none font-semibold transition-colors ${
                                                        !canToggleImage || isImageActionBusy || isRerunningLot || disablingLastActiveImage
                                                          ? 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                                                          : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-100'
                                                      }`}
                                                      title={
                                                        disablingLastActiveImage
                                                          ? 'At least one image must stay enabled'
                                                          : (isDisabledImage ? 'Turn on image for this lot' : 'Turn off image for this lot')
                                                      }
                                                    >
                                                      {isDisabledImage ? 'On' : 'Off'}
                                                    </button>
                                                  </div>
                                                );
                                              })}
                                              {resultImageUrls.length === 0 && (
                                                <div className="w-10 h-10 bg-slate-200 rounded flex items-center justify-center text-[10px] text-slate-400">No Img</div>
                                              )}
                                            </div>
                                            <div className="flex flex-col gap-1 mt-2">
                                              <button
                                                type="button"
                                                onClick={(event) => {
                                                  event.stopPropagation();
                                                  rerunSingleLot({
                                                    runId,
                                                    lotNumber: rowLotNumber || lotNumber,
                                                  });
                                                }}
                                                disabled={!canRerunLot || isRerunningLot}
                                                className={`inline-flex items-center rounded border px-2 py-1 text-[10px] font-semibold transition-colors ${
                                                  !canRerunLot || isRerunningLot
                                                    ? 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                                                    : 'border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100'
                                                }`}
                                                title={
                                                  !runId
                                                    ? 'This run has no run_id, rerun is unavailable.'
                                                    : (!lotNumber
                                                      ? 'Missing lot number.'
                                                      : (activeImageCount <= 0
                                                        ? 'Enable at least one image before rerun.'
                                                        : 'Rerun this lot with enabled images only'))
                                                }
                                              >
                                                <RefreshCcw size={10} className={`mr-1 ${isRerunningLot ? 'animate-spin' : ''}`} />
                                                {isRerunningLot ? 'Rerunning...' : 'Rerun lot'}
                                              </button>
                                              {Array.isArray(res.rerun_history) && res.rerun_history.length > 0 && (() => {
                                                const histKey = `${runId}-${ridx}`;
                                                const isHistOpen = !!expandedHistory[histKey];
                                                return (
                                                  <button
                                                    type="button"
                                                    onClick={(event) => {
                                                      event.stopPropagation();
                                                      setExpandedHistory(prev => ({ ...prev, [histKey]: !prev[histKey] }));
                                                    }}
                                                    className="inline-flex items-center rounded border border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100 px-2 py-1 text-[10px] font-semibold transition-colors whitespace-nowrap"
                                                    title="Show history of previous image combination results"
                                                  >
                                                    {isHistOpen ? <ChevronUp size={10} className="mr-0.5" /> : <ChevronDown size={10} className="mr-0.5" />}
                                                    Hist ({res.rerun_history.length})
                                                  </button>
                                                );
                                              })()}
                                            </div>
                                          </td>
                                        )}
                                        <td className="px-1.5 py-1 border-r font-medium">
                                          {(() => {
                                            const nameKey = `${runId}-${ridx}-${iidx}`;
                                            const isNameExpanded = !!expandedItemNames[nameKey];
                                            const nameText = rowItem.item || rowItem.item_name || '-';
                                            return (
                                              <div
                                                className={`break-words cursor-pointer ${isNameExpanded ? '' : 'line-clamp-2'}`}
                                                title={isNameExpanded ? undefined : nameText}
                                                onClick={() => setExpandedItemNames(prev => ({ ...prev, [nameKey]: !prev[nameKey] }))}
                                              >
                                                {nameText}
                                              </div>
                                            );
                                          })()}
                                        </td>
                                        <td className="px-1.5 py-1 border-r">
                                          {estimatedValue === null ? '-' : `$${estimatedValue.toFixed(2)}`}
                                        </td>
                                        {iidx === 0 && (
                                          <td className="px-1.5 py-1 border-r align-top font-bold" rowSpan={itemCount}>
                                            ${resultItems.reduce((sum, entry) => sum + (toFiniteNumber(entry?.estimated) || 0), 0).toFixed(2)}
                                          </td>
                                        )}
                                        {iidx === 0 && (
                                          <td className="px-1.5 py-1 border-r align-top" rowSpan={itemCount}>
                                            {actualValue === null ? '-' : `$${actualValue.toFixed(2)}`}
                                          </td>
                                        )}
                                        <td className="px-1.5 py-1 border-r text-slate-600">
                                          {(() => {
                                            const condition = rowItem?.condition || 'Unknown';
                                            const priceSource = rowItem?.price_source || 'model_knowledge';
                                            const sourceLabel = priceSource === 'ebay_rag' ? 'eBay RAG' : priceSource === 'image' ? 'Image' : 'Model Knowledge';
                                            const sourceBg = priceSource === 'ebay_rag' ? 'bg-blue-100 text-blue-700' : priceSource === 'image' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600';
                                            const condBg = condition === 'New' ? 'bg-green-100 text-green-700' : condition === 'Damaged' || condition === 'For Parts' ? 'bg-red-100 text-red-700' : condition === 'Unknown' ? 'bg-slate-100 text-slate-500' : 'bg-yellow-100 text-yellow-700';
                                            return (
                                              <div className="flex flex-col gap-1">
                                                <div className="flex items-center gap-1">
                                                  <span className="text-[8px] text-slate-400">Condition:</span>
                                                  <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${condBg}`}>{condition}</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                  <span className="text-[8px] text-slate-400">Price Src:</span>
                                                  <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${sourceBg}`}>{sourceLabel}</span>
                                                </div>
                                              </div>
                                            );
                                          })()}
                                        </td>
                                        <td className="px-1.5 py-1 border-r align-top">
                                          <div className="space-y-1">
                                            <textarea
                                              value={reviewNoteValue}
                                              onChange={(event) => {
                                                const nextValue = event.target.value.slice(0, 4000);
                                                setReviewNoteDrafts((previous) => ({
                                                  ...previous,
                                                  [reviewNoteDraftKey]: nextValue,
                                                }));
                                              }}
                                              maxLength={4000}
                                              placeholder="Add subitem note..."
                                              className="w-full min-h-[48px] rounded border border-slate-300 px-1.5 py-1 text-[10px] outline-none focus:border-blue-500"
                                            />
                                            <div className="flex items-center justify-between">
                                              <span className="text-[10px] text-slate-400">
                                                {reviewNoteValue.length}/4000
                                              </span>
                                              <button
                                                type="button"
                                                onClick={(event) => {
                                                  event.stopPropagation();
                                                  if (!canSaveReviewNote || isSavingReviewNote || !hasReviewNoteChanges) return;
                                                  saveReviewNote({
                                                    runId,
                                                    lotNumber: rowLotNumber || lotNumber,
                                                    resultIndex: ridx,
                                                    itemIndex: iidx,
                                                    reviewNote: reviewNoteValue,
                                                  });
                                                }}
                                                disabled={!canSaveReviewNote || isSavingReviewNote || !hasReviewNoteChanges}
                                                className={`rounded px-2 py-0.5 text-[10px] font-semibold transition-colors ${
                                                  !canSaveReviewNote || isSavingReviewNote || !hasReviewNoteChanges
                                                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'
                                                    : 'bg-blue-600 text-white hover:bg-blue-700 border border-blue-600'
                                                }`}
                                              >
                                                {isSavingReviewNote ? 'Saving...' : 'Save Note'}
                                              </button>
                                            </div>
                                          </div>
                                        </td>
                                        {iidx === 0 && (() => {
                                          const lotDraftKey = `lot-${runId}-${lotNumber || ridx}-${ridx}`;
                                          const persistedLotNote = (typeof res.lot_review_note === 'string') ? res.lot_review_note : '';
                                          const lotNoteValue = Object.prototype.hasOwnProperty.call(lotNoteDrafts, lotDraftKey) ? lotNoteDrafts[lotDraftKey] : persistedLotNote;
                                          const isSavingLotNote = !!savingLotNotes[lotDraftKey];
                                          const hasLotNoteChanges = lotNoteValue !== persistedLotNote;
                                          return (
                                            <td className="px-1.5 py-1 border-r align-top" rowSpan={itemCount}>
                                              <div className="space-y-1">
                                                <textarea
                                                  value={lotNoteValue}
                                                  onChange={(e) => {
                                                    const v = e.target.value.slice(0, 4000);
                                                    setLotNoteDrafts((prev) => ({ ...prev, [lotDraftKey]: v }));
                                                  }}
                                                  maxLength={4000}
                                                  placeholder="Add item note..."
                                                  className="w-full min-h-[48px] rounded border border-slate-300 px-1.5 py-1 text-[10px] outline-none focus:border-green-500"
                                                />
                                                <div className="flex items-center justify-between">
                                                  <span className="text-[10px] text-slate-400">{lotNoteValue.length}/4000</span>
                                                  <button
                                                    type="button"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      if (!canSaveReviewNote || isSavingLotNote || !hasLotNoteChanges) return;
                                                      saveLotReviewNote({
                                                        runId,
                                                        lotNumber: lotNumber || ridx,
                                                        resultIndex: ridx,
                                                        reviewNote: lotNoteValue,
                                                      });
                                                    }}
                                                    disabled={!canSaveReviewNote || isSavingLotNote || !hasLotNoteChanges}
                                                    className={`rounded px-2 py-0.5 text-[10px] font-semibold transition-colors ${
                                                      !canSaveReviewNote || isSavingLotNote || !hasLotNoteChanges
                                                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'
                                                        : 'bg-green-600 text-white hover:bg-green-700 border border-green-600'
                                                    }`}
                                                  >
                                                    {isSavingLotNote ? 'Saving...' : 'Save'}
                                                  </button>
                                                </div>
                                              </div>
                                            </td>
                                          );
                                        })()}
                                        {iidx === 0 && (() => {
                                          const aiSum = resultItems.reduce((sum, entry) => sum + (toFiniteNumber(entry?.estimated) || 0), 0);
                                          const actual = toFiniteNumber(rowItem.actual);
                                          const acc = computeAccuracyRatio(actual, aiSum);
                                          return (
                                            <td className="px-1.5 py-1 border-r align-top" rowSpan={itemCount}>
                                              {acc === null ? '-' : (
                                                <span className={`px-1 py-0.5 rounded font-bold text-[10px] ${
                                                  acc > 0.8 ? 'bg-green-100 text-green-700' : 
                                                  acc > 0.5 ? 'bg-yellow-100 text-yellow-700' : 
                                                  'bg-red-100 text-red-700'
                                                }`}>
                                                  {(acc * 100).toFixed(1)}%
                                                </span>
                                              )}
                                            </td>
                                          );
                                        })()}
                                        {iidx === 0 && (
                                          <td className="px-1.5 py-1 border-r align-top" rowSpan={itemCount}>
                                            {(() => {
                                              const durSec = toFiniteNumber(res.duration);
                                              const durMs = toFiniteNumber(res.duration_ms);
                                              const dur = durMs > 0 ? durMs / 1000 : (durSec > 0 ? durSec : 0);
                                              const liveDur = toFiniteNumber(res.last_live_duration);
                                              const displayDur = dur > 0 ? dur : liveDur;
                                              return displayDur != null && displayDur > 0 ? `${(displayDur).toFixed(1)}s` : '0ms';
                                            })()}
                                          </td>
                                        )}
                                        {iidx === 0 && (
                                          <td className="px-1.5 py-1 border-r align-top" rowSpan={itemCount}>
                                            <span
                                              className={`inline-flex rounded px-1 py-0.5 text-[9px] font-semibold whitespace-nowrap ${getSourceBadgeClassName(sourceInfo.finalSource, sourceInfo.finalCacheHit)}`}
                                              title={sourceInfo.cacheKey || undefined}
                                            >
                                              {sourceInfo.finalCacheHit === null ? getSourceDisplayLabel(sourceInfo.finalSource) : (sourceInfo.finalCacheHit ? 'CACHE' : 'LIVE')}
                                            </span>
                                          </td>
                                        )}
                                        {iidx === 0 && (
                                          <td className="px-1.5 py-1 border-r align-top" rowSpan={itemCount}>
                                            <span className={`inline-flex rounded px-1.5 py-0.5 text-[9px] font-semibold ${
                                              toFiniteCount(resultTokenTotals.input) !== null ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'
                                            }`}>
                                              {formatTokenCount(resultTokenTotals.input)}
                                            </span>
                                          </td>
                                        )}
                                        {iidx === 0 && (
                                          <td className="px-1.5 py-1 border-r align-top" rowSpan={itemCount}>
                                            <span className={`inline-flex rounded px-1.5 py-0.5 text-[9px] font-semibold ${
                                              toFiniteCount(resultTokenTotals.output) !== null ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-500'
                                            }`}>
                                              {formatTokenCount(resultTokenTotals.output)}
                                            </span>
                                          </td>
                                        )}
                                        {iidx === 0 && (
                                          <td className="px-1.5 py-1 align-top" rowSpan={itemCount}>
                                            {formatCostDisplay(res.cost)}
                                          </td>
                                        )}
                                      </tr>
                                    );
                                    })}
                                    {Array.isArray(res.rerun_history) && res.rerun_history.length > 0 && !!expandedHistory[`${runId}-${ridx}`] &&
                                      [...res.rerun_history].reverse().map((hist, hidx) => {
                                        const histItems = Array.isArray(hist?.items) && hist.items.length > 0 ? hist.items : [null];
                                        const histItemCount = histItems.length;
                                        const histImageUrls = getResultImageUrls(hist, histItems[0]);
                                        const histDisabledKeys = getDisabledImageKeySet(hist);
                                        const histEstSum = histItems.reduce((s, e) => s + (toFiniteNumber(e?.estimated) || 0), 0);
                                        const histActual = toFiniteNumber(histItems[0]?.actual);
                                        const histTokens = getResultTokenCounts(hist);
                                        const histSourceInfo = getResultAttemptSourceInfo(hist);
                                        const histAcc = computeAccuracyRatio(histActual, histEstSum);
                                        return histItems.map((hItem, hiidx) => {
                                          const hi = hItem && typeof hItem === 'object' ? hItem : {};
                                          const hiEst = toFiniteNumber(hi.estimated);
                                          return (
                                          <tr key={`${ridx}-hist-${hidx}-${hiidx}`} className={`bg-purple-50/60 ${hiidx < histItemCount - 1 ? '' : 'border-b border-purple-200'}`}>
                                            {hiidx === 0 && (
                                              <td className="px-1.5 py-1 border-r align-top bg-purple-100/50" rowSpan={histItemCount}>
                                                <div className="flex flex-col items-start gap-0.5">
                                                  <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-purple-600 text-white font-bold text-[8px] shrink-0">
                                                    {hidx + 1}
                                                  </span>
                                                  {hist.rerun_timestamp && (
                                                    <span className="text-[8px] text-purple-500 leading-tight text-center">
                                                      <span className="block">{new Date(hist.rerun_timestamp).toLocaleDateString(undefined, {month:'short',day:'numeric'})}</span>
                                                      <span className="block">{new Date(hist.rerun_timestamp).getFullYear()}</span>
                                                      <span className="block">{new Date(hist.rerun_timestamp).toLocaleTimeString(undefined, {hour:'2-digit',minute:'2-digit'})}</span>
                                                    </span>
                                                  )}
                                                </div>
                                              </td>
                                            )}
                                            {hiidx === 0 && (
                                              <td className="px-1.5 py-1 border-r align-top" rowSpan={histItemCount}>
                                                <div className="flex flex-wrap gap-1">
                                                  {histImageUrls.map((url, huidx) => {
                                                    const hImgKey = normalizeImageKey(url);
                                                    const hIsDisabled = histDisabledKeys.has(hImgKey);
                                                    return (
                                                      <div key={huidx} className="relative">
                                                        <img
                                                          src={url}
                                                          alt={`hist ${huidx + 1}`}
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedImage(url);
                                                            setSelectedImages(histImageUrls);
                                                            setSelectedLotNum(getResultLotNumber(res, histItems[0]) || lotNumber);
                                                            setSelectedItems(hist.items);
                                                          }}
                                                          className={`w-10 h-10 object-cover rounded shadow-sm hover:scale-[1.5] hover:z-10 transition-transform cursor-pointer ${hIsDisabled ? 'grayscale opacity-35' : ''}`}
                                                        />
                                                        {hIsDisabled && (
                                                          <span className="absolute inset-0 flex items-center justify-center text-[7px] font-bold text-white bg-slate-900/55 rounded">OFF</span>
                                                        )}
                                                      </div>
                                                    );
                                                  })}
                                                </div>
                                              </td>
                                            )}
                                            <td className="px-1.5 py-1 border-r font-medium">
                                              {(() => { const hNameKey = `hist-${run.id}-${ridx}-${hidx}-${hiidx}`; const isHNameExp = !!expandedItemNames[hNameKey]; return (
                                                <div className={`break-words cursor-pointer ${isHNameExp ? '' : 'line-clamp-2'}`} title={isHNameExp ? '' : (hi.item || hi.item_name || '-')} onClick={() => setExpandedItemNames(prev => ({ ...prev, [hNameKey]: !prev[hNameKey] }))}>{hi.item || hi.item_name || '-'}</div>
                                              ); })()}
                                            </td>
                                            <td className="px-1.5 py-1 border-r">
                                              {hiEst === null ? '-' : `$${hiEst.toFixed(2)}`}
                                            </td>
                                            {hiidx === 0 && (
                                              <td className="px-1.5 py-1 border-r align-top font-bold" rowSpan={histItemCount}>
                                                ${histEstSum.toFixed(2)}
                                              </td>
                                            )}
                                            {hiidx === 0 && (
                                              <td className="px-1.5 py-1 border-r align-top" rowSpan={histItemCount}>
                                                {histActual === null ? '-' : `$${histActual.toFixed(2)}`}
                                              </td>
                                            )}
                                            <td className="px-1.5 py-1 border-r text-slate-600">
                                              {(() => {
                                                const histExplKey = `hist-${runId}-${ridx}-${hidx}-${hiidx}`;
                                                const hCondition = hi?.condition || 'Unknown';
                                                const hPriceSource = hi?.price_source || 'model_knowledge';
                                                const hSourceLabel = hPriceSource === 'ebay_rag' ? 'eBay RAG' : hPriceSource === 'image' ? 'Image' : 'Model Knowledge';
                                                const hSourceBg = hPriceSource === 'ebay_rag' ? 'bg-blue-100 text-blue-700' : hPriceSource === 'image' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600';
                                                const hCondBg = hCondition === 'New' ? 'bg-green-100 text-green-700' : hCondition === 'Damaged' || hCondition === 'For Parts' ? 'bg-red-100 text-red-700' : hCondition === 'Unknown' ? 'bg-slate-100 text-slate-500' : 'bg-yellow-100 text-yellow-700';
                                                return (
                                                  <div className="flex flex-col gap-1">
                                                    <div className="flex items-center gap-1">
                                                      <span className="text-[8px] text-slate-400">Condition:</span>
                                                      <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${hCondBg}`}>{hCondition}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                      <span className="text-[8px] text-slate-400">Price Src:</span>
                                                      <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${hSourceBg}`}>{hSourceLabel}</span>
                                                    </div>
                                                  </div>
                                                );
                                              })()}
                                            </td>
                                            <td className="px-1.5 py-1 border-r align-top">
                                              {(() => {
                                                const histReviewDraftKey = getReviewNoteDraftKey({
                                                  runId,
                                                  lotNumber: getResultLotNumber(res, histItems[0]) || lotNumber || ridx,
                                                  resultIndex: ridx,
                                                  itemIndex: hiidx,
                                                  historyIndex: hidx,
                                                });
                                                const histPersistedNote = getTrustedReviewNote(hi);
                                                const histNoteValue = Object.prototype.hasOwnProperty.call(reviewNoteDrafts, histReviewDraftKey)
                                                  ? reviewNoteDrafts[histReviewDraftKey]
                                                  : histPersistedNote;
                                                const histIsSaving = !!savingReviewNotes[histReviewDraftKey];
                                                const histHasChanges = histNoteValue !== histPersistedNote;
                                                const histCanSave = Boolean(runId && (getResultLotNumber(res, histItems[0]) || lotNumber));
                                                return (
                                                  <div className="space-y-1">
                                                    <textarea
                                                      value={histNoteValue}
                                                      onChange={(event) => {
                                                        const nextValue = event.target.value.slice(0, 4000);
                                                        setReviewNoteDrafts((previous) => ({
                                                          ...previous,
                                                          [histReviewDraftKey]: nextValue,
                                                        }));
                                                      }}
                                                      maxLength={4000}
                                                      placeholder="Add subitem note..."
                                                      className="w-full min-h-[48px] rounded border border-purple-300 px-1.5 py-1 text-[10px] outline-none focus:border-purple-500 bg-purple-50/30"
                                                    />
                                                    <div className="flex items-center justify-between">
                                                      <span className="text-[10px] text-slate-400">
                                                        {histNoteValue.length}/4000
                                                      </span>
                                                      <button
                                                        type="button"
                                                        onClick={(event) => {
                                                          event.stopPropagation();
                                                          if (!histCanSave || histIsSaving || !histHasChanges) return;
                                                          saveReviewNote({
                                                            runId,
                                                            lotNumber: getResultLotNumber(res, histItems[0]) || lotNumber || ridx,
                                                            resultIndex: ridx,
                                                            itemIndex: hiidx,
                                                            historyIndex: hidx,
                                                            reviewNote: histNoteValue,
                                                          });
                                                        }}
                                                        disabled={!histCanSave || histIsSaving || !histHasChanges}
                                                        className={`rounded px-2 py-0.5 text-[10px] font-semibold transition-colors ${
                                                          !histCanSave || histIsSaving || !histHasChanges
                                                            ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'
                                                            : 'bg-purple-600 text-white hover:bg-purple-700 border border-purple-600'
                                                        }`}
                                                      >
                                                        {histIsSaving ? 'Saving...' : 'Save Note'}
                                                      </button>
                                                    </div>
                                                  </div>
                                                );
                                              })()}
                                            </td>
                                            {hiidx === 0 && (() => {
                                              const histLotDraftKey = `lot-${runId}-${lotNumber || ridx}-${ridx}-h${hidx}`;
                                              const histPersistedLotNote = (typeof hist.lot_review_note === 'string') ? hist.lot_review_note : '';
                                              const histLotNoteValue = Object.prototype.hasOwnProperty.call(lotNoteDrafts, histLotDraftKey) ? lotNoteDrafts[histLotDraftKey] : histPersistedLotNote;
                                              const histIsSavingLotNote = !!savingLotNotes[histLotDraftKey];
                                              const histHasLotNoteChanges = histLotNoteValue !== histPersistedLotNote;
                                              const histLotCanSave = Boolean(runId && (getResultLotNumber(res, histItems[0]) || lotNumber));
                                              return (
                                                <td className="px-1.5 py-1 border-r align-top" rowSpan={histItemCount}>
                                                  <div className="space-y-1">
                                                    <textarea
                                                      value={histLotNoteValue}
                                                      onChange={(e) => {
                                                        const v = e.target.value.slice(0, 4000);
                                                        setLotNoteDrafts((prev) => ({ ...prev, [histLotDraftKey]: v }));
                                                      }}
                                                      maxLength={4000}
                                                      placeholder="Add item note..."
                                                      className="w-full min-h-[48px] rounded border border-purple-300 px-1.5 py-1 text-[10px] outline-none focus:border-purple-500 bg-purple-50/30"
                                                    />
                                                    <div className="flex items-center justify-between">
                                                      <span className="text-[10px] text-slate-400">{histLotNoteValue.length}/4000</span>
                                                      <button
                                                        type="button"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          if (!histLotCanSave || histIsSavingLotNote || !histHasLotNoteChanges) return;
                                                          saveLotReviewNote({
                                                            runId,
                                                            lotNumber: getResultLotNumber(res, histItems[0]) || lotNumber || ridx,
                                                            resultIndex: ridx,
                                                            historyIndex: hidx,
                                                            reviewNote: histLotNoteValue,
                                                          });
                                                        }}
                                                        disabled={!histLotCanSave || histIsSavingLotNote || !histHasLotNoteChanges}
                                                        className={`rounded px-2 py-0.5 text-[10px] font-semibold transition-colors ${
                                                          !histLotCanSave || histIsSavingLotNote || !histHasLotNoteChanges
                                                            ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'
                                                            : 'bg-purple-600 text-white hover:bg-purple-700 border border-purple-600'
                                                        }`}
                                                      >
                                                        {histIsSavingLotNote ? 'Saving...' : 'Save'}
                                                      </button>
                                                    </div>
                                                  </div>
                                                </td>
                                              );
                                            })()}
                                            {hiidx === 0 && (() => {
                                              return (
                                                <td className="px-1.5 py-1 border-r align-top" rowSpan={histItemCount}>
                                                  {histAcc !== null ? (
                                                    <span className={`px-1 py-0.5 rounded font-bold text-[10px] ${
                                                      histAcc > 0.8 ? 'bg-green-100 text-green-700' :
                                                      histAcc > 0.5 ? 'bg-yellow-100 text-yellow-700' :
                                                      'bg-red-100 text-red-700'
                                                    }`}>
                                                      {(histAcc * 100).toFixed(1)}%
                                                    </span>
                                                  ) : '-'}
                                                </td>
                                              );
                                            })()}
                                            {hiidx === 0 && (() => {
                                              const histDurSec = toFiniteNumber(hist.duration);
                                              const histDurMs = toFiniteNumber(hist.duration_ms);
                                              const histDur = histDurMs > 0 ? histDurMs / 1000 : (histDurSec > 0 ? histDurSec : 0);
                                              const liveDur = toFiniteNumber(hist.last_live_duration);
                                              const displayDur = histDur > 0 ? histDur : liveDur;
                                              return (
                                              <td className="px-1.5 py-1 border-r align-top" rowSpan={histItemCount}>
                                                {displayDur != null && displayDur > 0 ? `${Number(displayDur).toFixed(1)}s` : '-'}
                                              </td>
                                              );
                                            })()}
                                            {hiidx === 0 && (
                                              <td className="px-1.5 py-1 border-r align-top" rowSpan={histItemCount}>
                                                <span
                                                  className={`inline-flex rounded px-1 py-0.5 text-[9px] font-semibold whitespace-nowrap ${getSourceBadgeClassName(histSourceInfo.finalSource, histSourceInfo.finalCacheHit)}`}
                                                  title={histSourceInfo.cacheKey || undefined}
                                                >
                                                  {histSourceInfo.finalCacheHit === null ? getSourceDisplayLabel(histSourceInfo.finalSource) : (histSourceInfo.finalCacheHit ? 'CACHE' : 'LIVE')}
                                                </span>
                                              </td>
                                            )}
                                            {hiidx === 0 && (
                                              <td className="px-1.5 py-1 border-r align-top" rowSpan={histItemCount}>
                                                <span className={`inline-flex rounded px-1.5 py-0.5 text-[9px] font-semibold ${
                                                  toFiniteCount(histTokens.input) !== null ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'
                                                }`}>
                                                  {formatTokenCount(histTokens.input)}
                                                </span>
                                              </td>
                                            )}
                                            {hiidx === 0 && (
                                              <td className="px-1.5 py-1 border-r align-top" rowSpan={histItemCount}>
                                                <span className={`inline-flex rounded px-1.5 py-0.5 text-[9px] font-semibold ${
                                                  toFiniteCount(histTokens.output) !== null ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-500'
                                                }`}>
                                                  {formatTokenCount(histTokens.output)}
                                                </span>
                                              </td>
                                            )}
                                            {hiidx === 0 && (
                                              <td className="px-1.5 py-1 align-top" rowSpan={histItemCount}>
                                                {formatCostDisplay(hist.cost)}
                                              </td>
                                            )}
                                          </tr>
                                          );
                                        });
                                      })
                                    }
                                  </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {loading && (
              <tr>
                <td colSpan="15" className="p-8 text-center text-slate-400 italic">Loading test runs...</td>
              </tr>
            )}
            {!loading && dataLoadError && (
              <tr>
                <td colSpan="15" className="p-8 text-center text-red-500 italic">
                  Failed to load test runs: {dataLoadError}
                </td>
              </tr>
            )}
            {!loading && !dataLoadError && displayedTestResults.length === 0 && activeRuns.length === 0 && (
              <tr>
                <td colSpan="15" className="p-8 text-center text-slate-400 italic">No test runs found yet.</td>
              </tr>
            )}
            {!loading && !dataLoadError && (displayedTestResults.length > 0 || activeRuns.length > 0) && filteredDisplayedTestResults.length === 0 && filteredActiveRuns.length === 0 && (
              <tr>
                <td colSpan="15" className="p-8 text-center text-slate-400 italic">
                  No test runs match {runSearchSummary || 'the current filters'}.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {filteredDisplayedTestResults.length > itemsPerPage && (
        <div className="flex flex-col sm:flex-row justify-between items-center pt-6 border-t border-slate-100 gap-4">
          <div className="text-sm text-slate-500">
            Showing <span className="font-medium">{showingFirstRunNumber}</span> to <span className="font-medium">{showingLastRunNumber}</span> of <span className="font-medium">{filteredDisplayedTestResults.length}</span> runs
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center mr-4">
              <span className="text-sm text-slate-500 mr-2">Show:</span>
              <select
                value={itemsPerPage}
                onChange={(event) => {
                  setItemsPerPage(Number(event.target.value));
                  setCurrentPage(1);
                  setExpandedRun(null);
                }}
                className="text-sm border border-slate-200 rounded px-2 py-1 outline-none focus:border-blue-500"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
            <nav className="flex items-center gap-1">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-2 rounded border ${currentPage === 1 ? 'text-slate-300 border-slate-100 cursor-not-allowed' : 'text-slate-600 border-slate-200 hover:bg-slate-50'}`}
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                .map((page, index, array) => (
                  <React.Fragment key={page}>
                    {index > 0 && array[index - 1] !== page - 1 && (
                      <span className="px-2 text-slate-400">...</span>
                    )}
                    <button
                      onClick={() => paginate(page)}
                      className={`min-w-[32px] h-8 rounded text-sm font-medium transition-colors ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                ))
              }
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-2 rounded border ${currentPage === totalPages ? 'text-slate-300 border-slate-100 cursor-not-allowed' : 'text-slate-600 border-slate-200 hover:bg-slate-50'}`}
              >
                <ChevronRight size={16} />
              </button>
            </nav>
          </div>
        </div>
      )}
      <ImageModal 
        isOpen={!!selectedImage} 
        onClose={() => {
            setSelectedImage(null);
            setSelectedImages([]);
            setSelectedLotNum(null);
            setSelectedItems(null);
        }}
        imageUrl={selectedImage}
        imageUrls={selectedImages}
        lotNum={selectedLotNum}
        items={selectedItems}
      />
    </div>
  );
};

export default TestRigPage;
